import { useRef } from "react"
import { ToBeSaved } from "lib/@components/save-indicator/save-indicator"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { createTailoredAuditReason } from "lib/@offline/queued-audits"
import { shouldAutoSaveCommentJustification } from "./should-auto-save-comment-justification"

// Shared engine for "when a subject (task / intro / intro notes) carries a share-link comment, its
// justification IS the comment text — so once the host changes it, persist the justification
// automatically." It arms off the tailoring-save dirty event, then persists the audit the caller builds
// once that same save cycle reports success. Resolving or refetching a comment never raises the dirty
// event, so it can never trigger an auto-save.
export function useAutoJustifyFromComment({
    subjectKey,
    tailored,
    isHeader = false,
    isEditable = true,
    hasComment,
    hasExistingReason,
    buildAudit,
}) {
    const savedForKey = useRef(null)
    const sawInSessionChange = useRef(false)
    const activeSubjectKey = useRef(subjectKey)
    if (activeSubjectKey.current !== subjectKey) {
        activeSubjectKey.current = subjectKey
        savedForKey.current = null
        sawInSessionChange.current = false
    }

    // The save-complete event fires outside render, so it must read fresh values rather than close over a
    // stale render's props.
    const latest = useRef(null)
    latest.current = { subjectKey, tailored, isHeader, isEditable, hasComment, hasExistingReason, buildAudit }

    ToBeSaved("tailor").useEvent(onTailoringSaveState, [])

    function onTailoringSaveState(value) {
        if (value === true) {
            sawInSessionChange.current = true
            return
        }
        if (value === false) runAutoSave()
    }

    function runAutoSave() {
        const snapshot = latest.current
        if (!snapshot.subjectKey) return

        const allowed = shouldAutoSaveCommentJustification({
            sawInSessionChange: sawInSessionChange.current,
            isEditable: snapshot.isEditable,
            pendingChange: false,
            taskTailored: snapshot.tailored,
            isHeader: snapshot.isHeader,
            hasComment: snapshot.hasComment,
            hasExistingReason: snapshot.hasExistingReason,
            alreadySaved: savedForKey.current === snapshot.subjectKey,
        })
        if (!allowed) return
        savedForKey.current = snapshot.subjectKey
        persist(snapshot)
    }

    async function persist(snapshot) {
        try {
            await createTailoredAuditReason(snapshot.buildAudit())
            successSnackbar("Justification saved from the comment")
        } catch {
            savedForKey.current = null
            errorSnackbar("Couldn't auto-save the justification from the comment")
        }
    }
}
